
import './Notifications.css';

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import {
    updateNotificationRequest,
    updateAllNotificationsRequest
} from '../../services/notificationServices/notificationService';

import { getSessionItem } from '../../utils/helper/session';

function Notifications({ messages = [], isLoading = false }) {
    const navigate = useNavigate();
    const listRef = useRef(null);

    const [isOpen, setIsOpen] = useState(false);
    const [localMessages, setLocalMessages] = useState(messages);
    const [isClearingAll, setIsClearingAll] = useState(false);

    const loggedUser = getSessionItem('User');
    const userEmail = loggedUser?.email || loggedUser?.username;

    const unreadMessages = localMessages.filter(
        message => !message.isRead
    );

    const unreadNotifications = unreadMessages.length;
    const hasNotifications = unreadNotifications > 0;
    const isDisabled = isLoading || !hasNotifications || isClearingAll;

    const toggleDropdown = () => {
        if (isDisabled) return;

        setIsOpen(prev => !prev);
    };

    async function handleNotificationClick(id, path) {
        if (isLoading || isClearingAll) return;

        try {
            await updateNotificationRequest(id);

            setLocalMessages(prev =>
                prev.filter(message => message._id !== id)
            );

            if (path) {
                navigate(path);
            }
        } catch (error) {
            console.error('Failed to handle notification click:', error);
        }
    }

    async function handleClearAll() {
        if (isLoading || isClearingAll || !userEmail || !hasNotifications) return;

        setIsClearingAll(true);

        try {
            await updateAllNotificationsRequest(userEmail);

            setLocalMessages([]);
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to clear notifications:', error);
        } finally {
            setIsClearingAll(false);
        }
    }

    function confirmClearAll() {
        if (isLoading || isClearingAll || !userEmail || !hasNotifications) return;

        confirmDialog({
            group: 'clearAllNotifications',
            message: 'Are you sure you want to mark all notifications as read?',
            header: 'Clear All Notifications',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, clear all',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-danger',
            rejectClassName: 'p-button-text p-button-secondary',
            accept: handleClearAll
        });
    }

    useEffect(() => {
        setLocalMessages(messages);

        if (listRef.current) {
            listRef.current.scrollTop = 0;
        }
    }, [messages]);

    useEffect(() => {
        if (!hasNotifications || isLoading) {
            setIsOpen(false);
        }
    }, [hasNotifications, isLoading]);

    return (
        <div
            className={`notification-container ${isDisabled ? 'disabled' : ''}`}
            id="header-notifications"
        >
            <ConfirmDialog group="clearAllNotifications" />

            <div
                className="notification-icon"
                onClick={toggleDropdown}
                title={
                    isLoading
                        ? 'Loading notifications...'
                        : hasNotifications
                            ? `${unreadNotifications} unread notification${unreadNotifications !== 1 ? 's' : ''}`
                            : '0 notifications'
                }
                aria-busy={isLoading}
            >
                {isLoading ? (
                    <ProgressSpinner
                        className="notification-loading-spinner"
                        strokeWidth="5"
                        animationDuration=".8s"
                    />
                ) : (
                    <>
                        <NotificationsIcon
                            fontSize="large"
                            color="inherit"
                        />

                        <span className="notification-count">
                            {unreadNotifications}
                        </span>
                    </>
                )}
            </div>

            {!isLoading && isOpen && hasNotifications && (
                <div className="notification-dropdown">
                    <ul
                        className="notification-list"
                        ref={listRef}
                    >
                        {unreadMessages.map(message => (
                            <li key={message._id}>
                                <Button
                                    label={message.content}
                                    className={
                                        message.path
                                            ? 'notif-btn'
                                            : 'notif-btn no-link'
                                    }
                                    onClick={() =>
                                        handleNotificationClick(
                                            message._id,
                                            message.path
                                        )
                                    }
                                    disabled={isClearingAll}
                                />
                            </li>
                        ))}
                    </ul>

                    <div className="notifications-footer">
                        <Button
                            label="Clear all"
                            icon="pi pi-check-circle"
                            className="notifications-clear-all-btn"
                            onClick={confirmClearAll}
                            loading={isClearingAll}
                            disabled={isClearingAll || !userEmail}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default Notifications;
